/**
 * Error Handler Service
 *
 * Centralized error handling for API errors
 * Maps HTTP errors to user-friendly messages
 */

import {
  ApiError,
  AuthenticationError,
  AuthorizationError,
  NetworkError,
  NotFoundError,
  RateLimitError,
  ServerError,
  TimeoutError,
  UnknownError,
  ValidationError,
} from "../types/errors";
import axios, { AxiosError } from "axios";

export class ErrorHandlerService {
  /**
   * Convert axios error to ApiError
   */
  static handleError(error: unknown): ApiError {
    // Handle network errors (offline, timeout, etc.)
    if (!axios.isAxiosError(error)) {
      if (error instanceof Error) {
        // Check for network-related errors
        if (
          error.message.includes("Network Error") ||
          error.message.includes("Failed to fetch")
        ) {
          return new NetworkError(
            "Network error. Please check your internet connection and try again.",
            error,
          );
        }
        if (
          error.message.includes("timeout") ||
          error.name === "TimeoutError"
        ) {
          return new TimeoutError(
            "Request timed out. Please try again.",
            error,
          );
        }
        return new UnknownError(error.message, undefined, undefined, error);
      }
      return new UnknownError("An unexpected error occurred");
    }

    const axiosError = error as AxiosError;

    // Handle timeout
    if (axiosError.code === "ECONNABORTED" || axiosError.code === "ETIMEDOUT") {
      return new TimeoutError("Request timed out. Please try again.", error);
    }

    // Handle network errors (no response)
    if (!axiosError.response) {
      if (
        axiosError.code === "ERR_NETWORK" ||
        axiosError.message === "Network Error"
      ) {
        return new NetworkError(
          "Network error. Please check your internet connection and try again.",
          error,
        );
      }
      return new NetworkError(
        "Unable to connect to server. Please check your connection and try again.",
        error,
      );
    }

    const status = axiosError.response.status;
    const responseData = axiosError.response.data as any;

    // Extract error message from response
    let errorMessage = this.extractErrorMessage(responseData);

    // Map status codes to error types
    switch (status) {
      case 400:
        return new ValidationError(
          errorMessage || "Invalid request. Please check your input.",
          status,
          {
            ...responseData,
          },
          error,
        );

      case 401:
        return new AuthenticationError(
          errorMessage || "Authentication failed. Please log in again.",
          status,
          responseData,
          error,
        );

      case 403:
        return new AuthorizationError(
          errorMessage || "You do not have permission to perform this action.",
          status,
          responseData,
          error,
        );

      case 404:
        return new NotFoundError(
          errorMessage || "Resource not found.",
          status,
          responseData,
          error,
        );

      case 429:
        const retryAfter = axiosError.response.headers["retry-after"];
        return new RateLimitError(
          errorMessage ||
            `Too many requests. ${retryAfter ? `Please try again after ${retryAfter} seconds.` : "Please try again later."}`,
          status,
          { ...responseData, retryAfter },
          error,
        );

      case 409:
        // Conflict (e.g. duplicate slug/name) - treat as validation-style error
        return new ValidationError(
          errorMessage || "A conflict occurred. Please check your input.",
          status,
          {
            ...responseData,
          },
          error,
        );

      case 500:
      case 502:
      case 503:
      case 504:
        return new ServerError(
          errorMessage || "Server error occurred. Please try again later.",
          status,
          {
            ...responseData,
          },
          error,
        );

      default:
        return new UnknownError(
          errorMessage || `An error occurred (${status}). Please try again.`,
          status,
          responseData,
          error,
        );
    }
  }

  /**
   * Extract user-friendly error message from API response
   */
  private static extractErrorMessage(responseData: any): string | null {
    if (!responseData) {
      return null;
    }

    // Try different common error message fields
    if (typeof responseData === "string") {
      return responseData;
    }

    if (typeof responseData === "object") {
      // Common error message fields
      const messageFields = [
        "message",
        "error",
        "errorMessage",
        "msg",
        "detail",
        "details",
      ];

      for (const field of messageFields) {
        if (responseData[field]) {
          if (typeof responseData[field] === "string") {
            return responseData[field];
          }
          if (Array.isArray(responseData[field])) {
            return responseData[field].join(", ");
          }
        }
      }

      // Handle validation errors (array of errors)
      if (responseData.errors && Array.isArray(responseData.errors)) {
        return responseData.errors
          .map((err: any) => err.message || err)
          .join(", ");
      }

      // Handle nested error objects
      if (responseData.error && typeof responseData.error === "object") {
        return responseData.error.message || responseData.error.toString();
      }
    }

    return null;
  }

  /**
   * Get user-friendly error message for display
   */
  static getUserMessage(error: ApiError): string {
    return error.message;
  }
}
